import React, { useContext, useEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { LicenseManager } from "ag-grid-enterprise";
import { GridApi, GridReadyEvent } from "ag-grid-community";
import { useDispatch } from "react-redux";
import {
    agDefaultSidebar,
    agDefaultColDef,
    overlayLoadingTemplate,
    overlayNoRowsTemplate,
    mainContext,
} from "@ts/constants";
import {
    sortByStatusAndColumn,
    agFilterModified,
    onRowDataChanged,
    isExternalFilterPresent,
    doesExternalFilterPass,
    onFilterChanged,
    showCountOfDisplayedData,
    autoSizeAll,
    onMediaPlanGridModelUpdated,
} from "@ts/utils";
import { setGlobalGridApi } from "@features/gridApiSlice";
import { GridLayoutTypes, IMainContext } from "@ts/common-types";
import { Dispatch } from "redux";
import { setMediaPlanGridApiMap } from "@/features/gridDataSlice";

const MediaGridLayout: React.FC<GridLayoutTypes> = React.memo(
    ({
        columnDefs,
        rowData,
        loading,
        pagination,
        isTotalRowsReq,
        isRowSelectable,
        isSortByStatusReq,
        rowHeight,
        paginationPageSize,
        serverSideDatasource,
        rowModelType,
        serverSideStoreType,
        loadingCellRenderer,
        planLevel,
        excelStyles
    }) => {
        process.env.VITE_NODE_ENV === "production" &&
            LicenseManager.setLicenseKey(process.env.VITE_AGGRID_LICENSE_KEY);
        const { setIsCheckboxSelected } = useContext<IMainContext>(mainContext);
        const dispatch = useDispatch<Dispatch>();
        const [api, setApi] = useState<GridApi>();
        const ref = useRef(null);

        const onGridReady = () => {
            dispatch(setGlobalGridApi(ref.current.api));
            dispatch(
                setMediaPlanGridApiMap({
                    [planLevel]: ref.current.api,
                })
            );
            setApi(ref.current.api);
        };

        const onFilterModified = (event: Record<string, any>) =>
            agFilterModified(event);

        const onFirstDataRendered = (e: GridReadyEvent) => {
            isSortByStatusReq && sortByStatusAndColumn(e.columnApi);
        };

        const onSelectionChangedHandler = (event: GridReadyEvent) => {
            setIsCheckboxSelected(!!event.api.getSelectedNodes().length);
        };

        // For setting grid loaders and grid filter count badges
        useEffect(() => {
            if (api) {
                if (loading) {
                    api.showLoadingOverlay();
                } else {
                    if (rowData && rowData.length === 0) {
                        api.showNoRowsOverlay();
                    } else {
                        api.hideOverlay();
                        if (isTotalRowsReq) {
                            onMediaPlanGridModelUpdated({ api });
                        }
                    }
                    autoSizeAll(true, ref);
                }
                showCountOfDisplayedData(api);
            }
        }, [loading, rowData, api, isTotalRowsReq]);

        const gridProps = {
            ref,
            onGridReady,
            isTotalRowsReq,
            rowData,
            columnDefs,
            overlayLoadingTemplate,
            overlayNoRowsTemplate,
            onFilterModified,
            onFilterChanged,
            onFirstDataRendered,
            pagination,
            onRowDataChanged,
            isExternalFilterPresent,
            doesExternalFilterPass,
            isRowSelectable,
            rowHeight,
            paginationPageSize,
            serverSideDatasource,
            rowModelType,
            serverSideStoreType,
            loadingCellRenderer,
            excelStyles
        };

        return (
            <>
                <AgGridReact
                    {...gridProps}
                    sideBar={agDefaultSidebar}
                    defaultColDef={agDefaultColDef}
                    animateRows={true}
                    suppressRowClickSelection={true}
                    suppressScrollOnNewData={true}
                    onSelectionChanged={onSelectionChangedHandler}
                    rowSelection="multiple"
                ></AgGridReact>
            </>
        );
    }
);

export default MediaGridLayout;
